import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Mail, Lock, ArrowLeft, RefreshCw, KeyRound, CheckCircle } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ForgotPassword() {
  const [step, setStep] = useState(1); // 1: email, 2: OTP + new password, 3: success
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const { t } = useLanguage();
  const navigate = useNavigate();

  const handleRequestOTP = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      toast.success('If this email is registered, you will receive a reset code');
      setStep(2);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reset code');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        email,
        otp,
        new_password: newPassword
      });
      toast.success('Password reset successfully!');
      setStep(3);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    setResending(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      toast.success('New reset code sent!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resend code');
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-[#020617]">
      <div 
        className="absolute inset-0 bg-cover bg-center opacity-20"
        style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1543094152-7f8122e97b66?crop=entropy&cs=srgb&fm=jpg&q=85)' }}
      />
      <div className="absolute inset-0 bg-gradient-to-t from-[#020617] via-[#020617]/80 to-transparent" />
      
      <div className="relative w-full max-w-md">
        <div className="glass-card p-8">
          {step === 1 && (
            <>
              <Link
                to="/login"
                className="flex items-center text-slate-400 hover:text-white mb-4 transition-colors"
              >
                <ArrowLeft className="h-4 w-4 mr-1" />
                Back to login
              </Link>
              
              <div className="text-center mb-8">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#D4AF37]/20 flex items-center justify-center">
                  <KeyRound className="h-8 w-8 text-[#D4AF37]" />
                </div>
                <h1 className="font-heading text-2xl font-bold text-[#D4AF37] mb-2">Forgot Password?</h1>
                <p className="text-slate-400">Enter your email and we'll send you a reset code</p>
              </div>
              
              <form onSubmit={handleRequestOTP} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-slate-300">{t('email')}</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10 bg-slate-900/50 border-slate-700 focus:border-amber-500/50 text-white"
                      placeholder="you@example.com"
                      required
                      data-testid="forgot-email-input"
                    />
                  </div>
                </div>
                
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#D4AF37] text-slate-950 hover:bg-[#B5952F] font-semibold rounded-full py-6 transition-all duration-300 shadow-[0_0_10px_rgba(212,175,55,0.3)] hover:shadow-[0_0_20px_rgba(212,175,55,0.5)]"
                  data-testid="send-reset-code-btn"
                >
                  <Mail className="mr-2 h-4 w-4" />
                  {loading ? 'Sending...' : 'Send Reset Code'}
                </Button>
              </form>
            </>
          )}
          
          {step === 2 && (
            <>
              <button
                onClick={() => setStep(1)}
                className="flex items-center text-slate-400 hover:text-white mb-4 transition-colors"
              >
                <ArrowLeft className="h-4 w-4 mr-1" />
                Back
              </button>
              
              <div className="text-center mb-8">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#D4AF37]/20 flex items-center justify-center">
                  <Lock className="h-8 w-8 text-[#D4AF37]" />
                </div>
                <h1 className="font-heading text-2xl font-bold text-[#D4AF37] mb-2">Reset Password</h1>
                <p className="text-slate-400">
                  Enter the code sent to<br />
                  <span className="text-white font-medium">{email}</span>
                </p>
              </div>
              
              <form onSubmit={handleResetPassword} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="otp" className="text-slate-300">Verification Code</Label>
                  <Input
                    id="otp"
                    type="text"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="text-center text-2xl tracking-[0.5em] bg-slate-900/50 border-slate-700 focus:border-amber-500/50 text-white font-mono"
                    placeholder="000000"
                    maxLength={6}
                    required
                    data-testid="reset-otp-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="newPassword" className="text-slate-300">New Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                    <Input
                      id="newPassword"
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="pl-10 bg-slate-900/50 border-slate-700 focus:border-amber-500/50 text-white"
                      placeholder="••••••••"
                      required
                      minLength={6}
                      data-testid="new-password-input"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword" className="text-slate-300">Confirm Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                    <Input
                      id="confirmPassword"
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="pl-10 bg-slate-900/50 border-slate-700 focus:border-amber-500/50 text-white"
                      placeholder="••••••••"
                      required
                      data-testid="confirm-password-input"
                    />
                  </div>
                </div>
                
                <Button
                  type="submit"
                  disabled={loading || otp.length !== 6}
                  className="w-full bg-[#D4AF37] text-slate-950 hover:bg-[#B5952F] font-semibold rounded-full py-6 transition-all duration-300 shadow-[0_0_10px_rgba(212,175,55,0.3)] hover:shadow-[0_0_20px_rgba(212,175,55,0.5)]"
                  data-testid="reset-password-btn"
                >
                  {loading ? 'Resetting...' : 'Reset Password'}
                </Button>
              </form>
              
              <div className="text-center mt-6">
                <p className="text-slate-500 text-sm mb-2">Didn't receive the code?</p>
                <button
                  onClick={handleResendOTP}
                  disabled={resending}
                  className="text-[#D4AF37] hover:underline flex items-center justify-center mx-auto"
                >
                  <RefreshCw className={`h-4 w-4 mr-1 ${resending ? 'animate-spin' : ''}`} />
                  {resending ? 'Sending...' : 'Resend Code'}
                </button>
              </div>
            </>
          )}
          
          {step === 3 && (
            <div className="text-center py-8">
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-green-500/20 flex items-center justify-center">
                <CheckCircle className="h-10 w-10 text-green-500" />
              </div>
              <h1 className="font-heading text-2xl font-bold text-white mb-2">Password Reset!</h1>
              <p className="text-slate-400 mb-8">Your password has been reset successfully</p>
              <Button
                onClick={() => navigate('/login')}
                className="bg-[#D4AF37] text-slate-950 hover:bg-[#B5952F] font-semibold rounded-full px-8 py-6 transition-all duration-300"
                data-testid="go-to-login-btn"
              >
                Go to Login
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
